import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";

export type ProjectFrameworkStructure = NonNullable<
  FunctionReturnType<typeof api.frameworks.getProjectStructure>
>;

// ============================================================================
// List Hooks (for list views)
// ============================================================================

export function useProjects(organizationId: Id<"orgs"> | null) {
  return useQuery(
    api.projects.listByOrganization,
    organizationId ? { orgId: organizationId } : "skip"
  );
}

export function useGroupedProjects(organizationId: Id<"orgs"> | null) {
  return useQuery(
    api.folders.listProjectsGrouped,
    organizationId ? { orgId: organizationId } : "skip"
  );
}

// ============================================================================
// Framework & Relations (for tree building)
// ============================================================================

/**
 * Fetch the active framework structure at organization scope.
 * Use this only on non-project surfaces (e.g. global onboarding views).
 */
export function useActiveOrgFrameworkStructure(orgId: Id<"orgs"> | null) {
  return useQuery(api.frameworks.getActiveStructure, orgId ? { orgId } : "skip");
}

/**
 * Fetch the framework structure assigned to a specific project.
 * Project surfaces must resolve from `project.frameworkReleaseId`.
 */
export function useProjectFrameworkStructure(projectId: Id<"projects"> | null) {
  return useQuery(api.frameworks.getProjectStructure, projectId ? { projectId } : "skip");
}

/**
 * Fetch all entity relations for a project.
 * Used by the tree builder to resolve parent→child links dynamically.
 */
export function useProjectRelations(projectId: Id<"projects"> | null) {
  return useQuery(api.frameworks.listProjectRelations, projectId ? { projectId } : "skip");
}

export function useProjectTreeFolders(projectId: Id<"projects"> | null) {
  return useQuery(api.projectTreeFolders.listByProject, projectId ? { projectId } : "skip");
}

export function useProjectTreeFolderBySlug(projectId: Id<"projects"> | null, slug: string | null) {
  return useQuery(
    api.projectTreeFolders.getBySlug,
    projectId && slug ? { projectId, slug } : "skip"
  );
}

/**
 * The project's active entities: identity, attributes and parent bindings, without tab content.
 * A surface that reads a field off each row of one type passes that type together with
 * `includeDocuments`; the query refuses content for a whole-project list.
 */
export function useProjectEntities(
  projectId: Id<"projects"> | null,
  entityTypeKey?: string | null,
  options: { includeDocuments?: boolean } = {}
) {
  return useQuery(
    api.projectEntities.listByProject,
    projectId
      ? {
          projectId,
          entityTypeKey: entityTypeKey ?? undefined,
          ...(options.includeDocuments && entityTypeKey ? { includeDocuments: true } : {}),
        }
      : "skip"
  );
}

export function useProjectEntityBySlug(
  projectId: Id<"projects"> | null,
  entityTypeKey: string | null,
  slug: string | null
) {
  return useQuery(
    api.projectEntities.getBySlug,
    projectId && entityTypeKey && slug
      ? {
          projectId,
          entityTypeKey,
          slug,
        }
      : "skip"
  );
}

export function useProjectEntity(entityTypeKey: string | null, entityId: string | null) {
  return useQuery(
    api.projectEntities.getById,
    entityTypeKey && entityId
      ? {
          entityTypeKey,
          entityId,
        }
      : "skip"
  );
}

/**
 * Fetch every file an entity references: uploads, images embedded in content, and
 * documents cited as evidence — deduped per artifact with a `sources` array.
 * Used by the entity-page Attachments tab.
 */
export function useEntityFiles(
  projectId: Id<"projects"> | null,
  entityTypeKey: string | null,
  entityId: string | null
) {
  return useQuery(
    api.artifacts.listEntityFiles,
    projectId && entityTypeKey && entityId
      ? {
          projectId,
          entityTypeKey,
          entityId,
        }
      : "skip"
  );
}

/**
 * The elements whose saved content mentions this one, and which of their sections does the
 * mentioning. Powers the generated "Used by" section (LAT-844).
 */
/** Everything the Usage tab shows for one element — both mention directions and relations. */
export function useEntityUsage(
  projectId: Id<"projects"> | null,
  entityTypeKey: string | null,
  entityId: string | null
) {
  return useQuery(
    api.entityUsage.get,
    projectId && entityTypeKey && entityId
      ? {
          projectId,
          entityTypeKey,
          entityId,
        }
      : "skip"
  );
}

/**
 * Per-file processing progress for every document a thread carries, as the chat attachment
 * chips render it. One subscription covers the whole thread: a file uploaded on its own is
 * queued in a processing group of its own, so asking any single group would answer for one
 * file and say nothing about the rest.
 */
export function useThreadAttachmentProcessing(
  projectId: Id<"projects"> | null,
  threadId: string | null,
  enabled = true
) {
  return useQuery(
    api.artifactProcessing.getActiveProcessingGroupForTarget,
    projectId && threadId && enabled
      ? {
          projectId,
          relationType: "assistant_attachment",
          target: {
            targetKind: "assistantThread",
            targetId: threadId,
            targetThreadId: threadId,
          },
        }
      : "skip"
  );
}

/**
 * Fetch children of a specific parent entity via entityRelations.
 * Used by entity detail views to show related children (e.g., objective → questions).
 */
export function useRelationsByParent(
  projectId: Id<"projects"> | null,
  parentTypeKey: string | null,
  parentEntityId: string | null
) {
  return useQuery(
    api.entityRelations.listByParent,
    projectId && parentTypeKey && parentEntityId
      ? { projectId, parentTypeKey, parentEntityId }
      : "skip"
  );
}

/**
 * Fetch parents of a specific child entity via entityRelations (reverse lookup).
 * Used to answer: "which objectives contain this question?"
 */
export function useRelationsByChild(
  projectId: Id<"projects"> | null,
  childTypeKey: string | null,
  childEntityId: string | null
) {
  return useQuery(
    api.entityRelations.listByChild,
    projectId && childTypeKey && childEntityId ? { projectId, childTypeKey, childEntityId } : "skip"
  );
}

export function useVersions(projectId: Id<"projects"> | null) {
  return useQuery(api.versions.listByProject, projectId ? { projectId, limit: 100 } : "skip");
}

export function useProjectSnapshots(projectId: Id<"projects"> | null) {
  return useQuery(api.versions.listProjectSnapshots, projectId ? { projectId } : "skip");
}

export function useProjectSnapshotDetails(snapshotId: Id<"projectSnapshots"> | null) {
  return useQuery(api.versions.getProjectSnapshotDetails, snapshotId ? { snapshotId } : "skip");
}

export function useCurrentProjectSnapshotDiff(projectId: Id<"projects"> | null) {
  return useQuery(api.versions.getCurrentProjectSnapshotDiff, projectId ? { projectId } : "skip");
}

/**
 * Fetch versions for a specific entity (dataset, KPI, objective, etc.).
 * Accepts optional currentUserId to filter drafts (show own, hide others').
 */
export function useVersionsByEntity(
  projectId: Id<"projects"> | null,
  entityTypeKey: string | null,
  entityId: string | null,
  currentUserId?: Id<"users"> | null
) {
  return useQuery(
    api.versions.listByEntity,
    projectId && entityTypeKey && entityId
      ? { projectId, entityTypeKey, entityId, currentUserId: currentUserId ?? undefined }
      : "skip"
  );
}

/**
 * "Has this element ever been saved?", for a handful of elements at once (LAT-1004).
 *
 * Backed by `versions.getCurrentVersionPresence`, which returns booleans only — presence of a
 * *committed* current version (drafts never qualify) — so landing screens can style untouched
 * singletons without subscribing to snapshot content. Ids are deduped and sorted before they
 * become query args, so two surfaces asking about the same set in a different order share one
 * subscription. Returns the raw rows; look entries up by `entityId`, not by index.
 */
export function useEntityVersionPresence(
  projectId: Id<"projects"> | null,
  entityIds: readonly Id<"entities">[] | null
) {
  const normalizedIds = entityIds && entityIds.length > 0 ? [...new Set(entityIds)].sort() : null;
  return useQuery(
    api.versions.getCurrentVersionPresence,
    projectId && normalizedIds ? { projectId, entityIds: normalizedIds } : "skip"
  );
}

/**
 * Describe the Claudia conversations behind the AI-origin versions on screen (LAT-938).
 *
 * Takes the *distinct* thread ids the panel actually renders, so version history costs one
 * query per panel open rather than one per row, and skips entirely when nothing on screen
 * came from the assistant.
 */
export function useVersionThreadProvenance(projectId: Id<"projects"> | null, threadIds: string[]) {
  return useQuery(
    api.versionProvenance.describeVersionThreadProvenance,
    projectId && threadIds.length > 0 ? { projectId, threadIds } : "skip"
  );
}

export function useEntityVersionByNumber(
  projectId: Id<"projects"> | null,
  entityTypeKey: string | null,
  entityId: string | null,
  versionNumber: number | null | undefined
) {
  return useQuery(
    api.versions.getByEntityVersionNumber,
    projectId && entityTypeKey && entityId && versionNumber !== null && versionNumber !== undefined
      ? { projectId, entityTypeKey, entityId, versionNumber }
      : "skip"
  );
}

/**
 * Fetch the current user's draft version for a specific entity.
 * Returns null if no draft exists, undefined while loading.
 */
export function useUserDraft(
  projectId: Id<"projects"> | null,
  entityTypeKey: string | null,
  entityId: string | null,
  createdBy: Id<"users"> | null
) {
  return useQuery(
    api.versions.getUserDraft,
    projectId && entityTypeKey && entityId && createdBy
      ? { projectId, entityTypeKey, entityId, createdBy }
      : "skip"
  );
}

// ============================================================================
// Single-Entity Hooks (for detail views)
// ============================================================================

export function useProject(projectId: Id<"projects"> | null) {
  return useQuery(api.projects.getById, projectId ? { projectId } : "skip");
}

export function useProjectBySlug(organizationId: Id<"orgs"> | null, slug: string | null) {
  return useQuery(
    api.projects.getBySlug,
    organizationId && slug ? { orgId: organizationId, slug } : "skip"
  );
}

/**
 * Locate a project slug across every organization the caller belongs to (LAT-627).
 *
 * Deliberately org-less: the owning organization is exactly what the caller is asking for.
 * Returns accessible matches (org + project identity) plus `existsElsewhere`, which is the
 * only signal for matches in organizations the caller is not a member of.
 */
export function useLocateProjectBySlug(slug: string | null) {
  return useQuery(api.projects.locateBySlug, slug ? { slug } : "skip");
}

export function useActiveTeamsTenant(organizationId: Id<"orgs"> | null) {
  return useQuery(
    api.teams.tenants.getActiveByOrg,
    organizationId ? { orgId: organizationId } : "skip"
  );
}
